import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ImageOff, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";

/** Editors-only health strip on the KB home: how much of the KB's imagery and
 *  linking still works. Numbers come from the weekly sweep, never a live check
 *  — so it always says WHEN it last looked rather than implying "right now". */
export const KbImageHealthCard = () => {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/kb/link-health/summary").then((r) => setS(r.data)).catch(() => setS({ error: true }));
  }, []);

  if (!s) {
    return <div className="mt-6 flex h-24 items-center justify-center rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
      data-testid="kb-image-health-loading">
      <Loader2 className="h-4 w-4 animate-spin text-slate-300 dark:text-slate-600" />
    </div>;
  }
  if (s.error) return null;

  const imgOk = s.images_ok || 0;
  const imgBad = (s.images_broken || 0) + (s.images_blocked || 0);
  const linkBad = (s.links_broken || 0) + (s.links_blocked || 0);
  const bars = [
    { key: "ok", n: imgOk, cls: "bg-emerald-500" },
    { key: "broken", n: s.images_broken || 0, cls: "bg-rose-500" },
    { key: "blocked", n: s.images_blocked || 0, cls: "bg-amber-400" },
  ];
  const total = Math.max(1, bars.reduce((a, b) => a + b.n, 0));
  const clean = !s.checked ? false : imgBad === 0 && linkBad === 0;

  return (
    <section className="mt-6" data-testid="kb-image-health-card">
      <Link to="/kb/image-health"
        className="group block rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-4 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
              clean ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
              <ImageOff className="h-4 w-4" />
            </span>
            <div>
              <h3 className="font-heading text-base font-semibold text-slate-800 dark:text-slate-100">Image &amp; link health</h3>
              {!s.checked ? (
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400" data-testid="kb-image-health-unchecked">
                  Nothing checked yet — open the screen and run the first sweep.
                </p>
              ) : (
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400" data-testid="kb-image-health-line">
                  {(s.last_status || "").startsWith("error") && (
                    <span className="mr-1 font-semibold text-rose-600" data-testid="kb-image-health-error">
                      Last check didn&apos;t finish —
                    </span>
                  )}
                  <span className="font-semibold text-slate-700 dark:text-slate-200">{imgOk.toLocaleString()}</span> images working
                  {(s.images_broken || 0) > 0 && <> · <span className="font-semibold text-rose-600">{s.images_broken} broken</span></>}
                  {(s.images_blocked || 0) > 0 && <> · <span className="font-semibold text-amber-600">{s.images_blocked} unverifiable</span></>}
                  {linkBad > 0 && <> · <span className="font-semibold text-slate-700 dark:text-slate-200">{linkBad} link{linkBad === 1 ? "" : "s"}</span> need a look</>}
                  {clean && <> · everything links up</>}
                  <span className="ml-1 text-slate-400 dark:text-slate-500">
                    {" "}— across {(s.articles_checked || 0).toLocaleString()} articles, last checked {(s.finished || "").slice(0, 10)}
                  </span>
                </p>
              )}
            </div>
          </div>
          <span className="flex items-center gap-1 text-xs font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
            Review <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
        {s.checked && (
          <div className="mt-3 flex h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" data-testid="kb-image-health-bar">
            {bars.filter((b) => b.n > 0).map((b) => (
              <span key={b.key} className={b.cls} style={{ width: `${(b.n / total) * 100}%` }} />
            ))}
          </div>
        )}
      </Link>
    </section>
  );
};
